import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dropdown, DropdownOption, Tag } from '@volvo/vcdk-react';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import type { RoleHandle } from './RoleHandle';
import type { IRoleIdentifier } from '@/service/vpc/types/IFleetAccountRole';
import type { IFleetRole } from '@/service/vpc/types/IFleetRoles';
import type { DropdownChangeEvent } from '@volvo/vcdk';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import { getAvailableFleetRolesQueryOptions, getFleetRolesQueryOptions, queryKeys } from '@/service/vpc/queryOptions';
import { vpcService } from '@/service/vpc/vpcService';

type Props = {
  label: string;
  isEdit: boolean;
  fleetId: string;
  endUserId: string;
};

const FleetRoles = forwardRef<RoleHandle, Props>(({ label, fleetId, endUserId, isEdit }, ref) => {
  // const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [selectedRole, setSelectedRole] = useState<IRoleIdentifier[]>([]);

  const { data: availableFleetRoles } = useQuery(getAvailableFleetRolesQueryOptions());
  const { data: fleetRoles } = useQuery(getFleetRolesQueryOptions({ fleetId }));

  const { mutateAsync: addFleetRoles } = useMutation({
    mutationFn: ({ fleetId, endUserId, roles }: { fleetId: string; endUserId: string; roles: string[] }) =>
      vpcService.Fleet.assignFleetRoles({ fleetId, endUserId, roles }),
    onSuccess() {
      // Invalidate or refetch queries related to roles if needed
      queryClient.invalidateQueries({
        queryKey: [queryKeys.getFleetRoles],
        exact: false,
      });
    },
  });

  const { mutateAsync: removeFleetRoles } = useMutation({
    mutationFn: ({ fleetId, roles }: { fleetId: string; roles: IFleetRole[] }) =>
      vpcService.Fleet.removeFleetRoles({ fleetId, roles }),
    onSuccess() {
      // Invalidate or refetch queries related to roles if needed
      queryClient.invalidateQueries({
        queryKey: [queryKeys.getFleetRoles],
        exact: false,
      });
    },
  });

  const handleSave = async () => {
    const currentRoles =
      fleetRoles?.items.filter((role) => role.endCustomerId === endUserId && role.fleetId === fleetId) || [];

    const removedRoles = currentRoles
      ?.filter((role) => role.role.roleType !== 'Member')
      .filter((role) => !selectedRole.includes(role.role.roleType));

    const newRoles = selectedRole.filter(
      (role) =>
        !currentRoles
          ?.filter((role) => role.role.roleType !== 'Member')
          .map((r) => r.role.roleType)
          .includes(role)
    );

    if (fleetId && newRoles.length) {
      await addFleetRoles({ fleetId, endUserId, roles: newRoles });
    }
    if (fleetId && removedRoles.length) {
      await removeFleetRoles({ fleetId, roles: removedRoles });
    }
  };

  // Expose the save function to parent
  useImperativeHandle(ref, () => ({
    save: handleSave,
  }));

  const currentFleetRoles =
    fleetRoles?.items.filter((role) => role.endCustomerId === endUserId && role.fleetId === fleetId) || [];
  // const highestRole = roles.getHighestRole(currentFleetRoles.map((r) => r.role.roleType)) || null;

  useEffect(() => {
    if (fleetRoles?.items) {
      const currentRoles = fleetRoles.items
        .filter((role) => role.role.roleType !== 'Member')
        .filter((role) => role.endCustomerId === endUserId && role.fleetId === fleetId)
        .map((role) => role.role.roleType);

      setSelectedRole(currentRoles);
    }
  }, [fleetRoles, fleetId, endUserId]);

  return isEdit ? (
    <Dropdown
      multi
      disableCloseOnSelect
      size="small"
      placement="top"
      label={label}
      valueAsArray={selectedRole}
      placeholder="Select roles"
      className="[&::part(input-wrapper)]:h-10 [&::part(input-wrapper)]:flex-nowrap"
      onChange={(e: DropdownChangeEvent) => {
        setSelectedRole(e.detail.valueAsArray.map((value) => value as IRoleIdentifier));
      }}
    >
      {availableFleetRoles?.map((role) => (
        <DropdownOption key={role} value={role}>
          {role}
        </DropdownOption>
      ))}
    </Dropdown>
  ) : (
    <>
      <div className="flex gap-2">
        {currentFleetRoles.length
          ? currentFleetRoles.map((role) => (
              <Tag key={`${role.fleetRoleId}-${role.endCustomerId}-${role.role.roleType}`} variant="neutral-subtle">
                {role.role.roleType}
              </Tag>
            ))
          : '-'}
      </div>
    </>
  );
});

export default withComponentErrorBoundary(FleetRoles);
